import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Avatar,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import useAuthStore from '../../store/useAuthStore';
import useSessionAuthStore from '../../store/authStore';
import { CODING_PROFILE_TYPE_OPTIONS, labelForCodingProfileType } from '../../utils/codingProfiles';
import { displayAcademicField } from '../../utils/academicProfileFromApi';
import CodingProfileFormDialog from '../../components/CodingProfileFormDialog';
import { PROFILE_BRANCH_MENU_ITEMS } from '../../utils/profileFormConstants';
import ProfileSubsectionCard from '../../components/ProfileSubsectionCard';
import { useNavigate } from 'react-router-dom';
import { getDefaultRoute } from '../../utils/getDefaultRoute';

const Profile = () => {
  const profile = useAuthStore((s) => s.profile);
  const profileLoading = useAuthStore((s) => s.profileLoading);
  const profileSaving = useAuthStore((s) => s.profileSaving);
  const profileError = useAuthStore((s) => s.profileError);
  const profileSuccess = useAuthStore((s) => s.profileSuccess);
  const fetchProfile = useAuthStore((s) => s.fetchProfile);
  const updateProfileField = useAuthStore((s) => s.updateProfileField);
  const saveProfile = useAuthStore((s) => s.saveProfile);
  const resetProfile = useAuthStore((s) => s.resetProfile);
  const addCodingProfile = useAuthStore((s) => s.addCodingProfile);
  const updateCodingProfile = useAuthStore((s) => s.updateCodingProfile);
  const removeCodingProfile = useAuthStore((s) => s.removeCodingProfile);
  const academicProfile = useSessionAuthStore((s) => s.academicProfile);

  const [codingDialogOpen, setCodingDialogOpen] = useState(false);
  const [codingDialogMode, setCodingDialogMode] = useState('add');
  const [codingDialogEditIndex, setCodingDialogEditIndex] = useState(null);
  const [codingDialogType, setCodingDialogType] = useState('');
  const [codingDialogUsername, setCodingDialogUsername] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const openAddCodingProfile = () => {
    setCodingDialogMode('add');
    setCodingDialogEditIndex(null);
    setCodingDialogType('');
    setCodingDialogUsername('');
    setCodingDialogOpen(true);
  };

  const openEditCodingProfile = (index) => {
    const list = profile?.codingProfiles ?? [];
    const row = list[index];
    if (!row) return;
    setCodingDialogMode('edit');
    setCodingDialogEditIndex(index);
    setCodingDialogType(row.type);
    setCodingDialogUsername(row.username);
    setCodingDialogOpen(true);
  };

  const handleCodingProfileSubmit = useCallback(
    ({ profileType, username, mode: submitMode }) => {
      if (submitMode === 'add') {
        addCodingProfile({ type: profileType, username });
        return;
      }
      if (submitMode === 'edit' && codingDialogEditIndex != null) {
        const list = profile?.codingProfiles ?? [];
        const cur = list[codingDialogEditIndex];
        if (!cur || cur.type !== profileType) return;
        updateCodingProfile(codingDialogEditIndex, { username });
      }
    },
    [addCodingProfile, updateCodingProfile, codingDialogEditIndex, profile?.codingProfiles],
  );

  const deleteCodingProfileAt = (index) => {
    removeCodingProfile(index);
  };

  const closeCodingDialog = () => setCodingDialogOpen(false);

  const codingProfileRows = useMemo(() => {
    const list = profile?.codingProfiles ?? [];
    return list.map((entry, index) => ({
      index,
      type: entry.type,
      username: entry.username,
      label: labelForCodingProfileType(entry.type),
    }));
  }, [profile?.codingProfiles]);

  const occupiedCodingProfileTypes = useMemo(
    () => (profile?.codingProfiles ?? []).map((p) => p.type),
    [profile?.codingProfiles],
  );

  const allCodingProfileSlotsUsed =
    occupiedCodingProfileTypes.length >= CODING_PROFILE_TYPE_OPTIONS.length;

  const handleChange = (e) => {
    const { name, value } = e.target;
    updateProfileField(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveProfile();
  };

  const handleCancel = () => {
    resetProfile();
    navigate(getDefaultRoute(profile.role, profile));
    setCodingDialogOpen(false);
  };


  useEffect(() => {
    if (profileSuccess) {
      setTimeout(() => {
        navigate(getDefaultRoute(profile.role, profile));
        useAuthStore.setState({ profileSuccess: '' });
      }, 3000);
    }
  }, [profileSuccess, navigate, profile]);

  if (profileLoading || !profile) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Avatar 
              src={profile.profilePic} 
              alt={profile.name}
              sx={{ width: 80, height: 80, mr: 3 }}
            />
            <Box>
              <Typography variant="h4" component="h1">
                My Profile
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                Manage your account details
              </Typography>
            </Box>
          </Box>

          {profileError && <Alert severity="error" sx={{ mb: 3 }}>{profileError}</Alert>}
          
          {profileSuccess && <Alert severity="success" sx={{ mb: 3 }}>{profileSuccess}</Alert>}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3}>
              {/* Read-only Fields */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={profile.name}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  value={profile.email}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Role"
                  value={profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  component="h2"
                  fontWeight={600}
                  sx={{ mt: 1, mb: 2, letterSpacing: '-0.01em' }}
                >
                  Academic Details
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Roll Number"
                      value={displayAcademicField(academicProfile?.rollNumber)}
                      InputProps={{ readOnly: true }}
                      variant="filled"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Year"
                      value={displayAcademicField(academicProfile?.currentYear)}
                      InputProps={{ readOnly: true }}
                      variant="filled"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Branch"
                      value={displayAcademicField(academicProfile?.branch, 'Not available')}
                      InputProps={{ readOnly: true }}
                      variant="filled"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Entry Type"
                      value={displayAcademicField(academicProfile?.entryType)}
                      InputProps={{ readOnly: true }}
                      variant="filled"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="College"
                      value={displayAcademicField(academicProfile?.college)}
                      InputProps={{ readOnly: true }}
                      variant="filled"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Branch / Department"
                  name="branch"
                  value={profile.branch}
                  onChange={handleChange}
                  helperText="Select your branch"
                >
                  {PROFILE_BRANCH_MENU_ITEMS.map(({ value, menuLabel }) => (
                    <MenuItem key={value} value={value}>
                      {menuLabel}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {profile.role === 'student' && <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Year of Study"
                  name="yearOfStudy"
                  type="number"
                  value={profile.yearOfStudy}
                  onChange={handleChange}
                  placeholder="e.g., 3"
                />
              </Grid>}
              {profile.role === 'student' && (
                <Grid item xs={12}>
                  <ProfileSubsectionCard
                    title="Coding profiles"
                    description="Optional links used for coding activity and integrations"
                    headerAction={
                      <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        onClick={openAddCodingProfile}
                        disabled={allCodingProfileSlotsUsed}
                        title={
                          allCodingProfileSlotsUsed
                            ? 'All platforms are linked — edit or remove one in the table first'
                            : undefined
                        }
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          letterSpacing: '-0.01em',
                          minWidth: { xs: '100%', sm: 'auto' },
                        }}
                      >
                        <Box
                          component="span"
                          sx={{ mr: 0.75, fontSize: '1.1rem', fontWeight: 400, lineHeight: 1 }}
                        >
                          +
                        </Box>
                        Add profile
                      </Button>
                    }
                    empty={codingProfileRows.length === 0}
                    emptyMessage="No coding profiles added yet"
                  >
                    {codingProfileRows.length > 0 ? (
                      <TableContainer
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Table size="small" aria-label="Coding profiles">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                              <TableCell sx={{ fontWeight: 600, py: 1.25, fontSize: '0.8125rem' }}>
                                Profile type
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, py: 1.25, fontSize: '0.8125rem' }}>
                                Username
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ fontWeight: 600, py: 1.25, width: 100, fontSize: '0.8125rem' }}
                              >
                                Actions
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {codingProfileRows.map((row) => (
                              <TableRow
                                key={`${row.type}-${row.index}`}
                                hover
                                sx={{ '&:last-child td': { borderBottom: 0 } }}
                              >
                                <TableCell sx={{ py: 1.25, fontSize: '0.875rem' }}>{row.label}</TableCell>
                                <TableCell
                                  sx={{
                                    py: 1.25,
                                    fontSize: '0.875rem',
                                    fontFamily: 'ui-monospace, monospace',
                                    wordBreak: 'break-all',
                                  }}
                                >
                                  {row.username}
                                </TableCell>
                                <TableCell align="right" sx={{ py: 0.5, whiteSpace: 'nowrap' }}>
                                  <IconButton
                                    type="button"
                                    size="small"
                                    color="primary"
                                    aria-label={`Edit ${row.label} profile`}
                                    onClick={() => openEditCodingProfile(row.index)}
                                    sx={{ mr: 0.25 }}
                                  >
                                    <EditIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                  <IconButton
                                    type="button"
                                    size="small"
                                    color="error"
                                    aria-label={`Remove ${row.label} profile`}
                                    onClick={() => deleteCodingProfileAt(row.index)}
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : null}
                  </ProfileSubsectionCard>
                </Grid>
              )}
              {profile.role !== 'student' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="GitHub Username"
                    name="githubUsername"
                    value={profile.githubUsername}
                    onChange={handleChange}
                    placeholder="e.g., octocat"
                    helperText="Used to link your projects to your GitHub profile"
                  />
                </Grid>
              )}
            </Grid>

            {profile.role === 'student' && (
              <CodingProfileFormDialog
                key={
                  codingDialogOpen
                    ? `${codingDialogMode}-${
                        codingDialogMode === 'edit'
                          ? `${codingDialogType}-${codingDialogEditIndex}`
                          : 'new'
                      }`
                    : 'closed'
                }
                open={codingDialogOpen}
                onClose={closeCodingDialog}
                mode={codingDialogMode}
                initialProfileType={codingDialogType}
                initialUsername={codingDialogUsername}
                occupiedProfileTypes={occupiedCodingProfileTypes}
                onSubmit={handleCodingProfileSubmit}
              />
            )}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                type="button"
                variant="outlined"
                size="large"
                onClick={handleCancel}
                disabled={profileSaving}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={profileSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={profileSaving}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {profileSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Profile;
